import {
  AccessTime,
  CheckCircle,
  DeliveryDining,
  DoNotDisturbOn,
  EditOutlined,
  ErrorOutline,
  Refresh,
  Storefront,
} from "@mui/icons-material";
import { Tab, Tabs } from "@mui/material";
import TimeSettingDialog from "components/admin/TimeSettingDialog";
import api from "config/api";
import { format } from "date-fns";
import { useAppSelector } from "hooks/store";
import { useEffect, useState } from "react";
import { convertNumberToHex } from "screens";
import { ORDER } from "utils/constants";
import { TimeItem } from "utils/types";

type DataType = {
  TakeOut: TimeItem[];
  Delivery: TimeItem[];
};

const emptyTimingData: DataType = { TakeOut: [], Delivery: [] };

const getApiErrorMessage = (error: any, fallback = "Unable to load restaurant timing") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error?.message === "string") return error.message;
  if (typeof error?.errorMessage === "string") return error.errorMessage;
  if (typeof error?.Message === "string") return error.Message;
  if (typeof error?.data?.message === "string") return error.data.message;
  return fallback;
};

const TimeSetting = () => {
  const [selectedTab, setSelectedTab] = useState<keyof DataType>(
    ORDER.TAKE_OUT
  );
  const [data, setData] = useState<DataType>(emptyTimingData);
  const [selectedData, setSelectedData] = useState<any>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const { settings } = useAppSelector(({ main }) => main);
  const themeColor = convertNumberToHex(settings.themeColor);

  const fetchData = () => {
    setLoading(true);
    setLoadError("");
    api
      .get("/api/RestTime/GetResttimes")
      .then(({ data }) => {
        if (!Array.isArray(data)) {
          throw data || "No restaurant timing data found";
        }

        setData(
          data.reduce(
            (obj: any, item: any) => ({
              ...obj,
              [item.timOrdtype]: [...(obj[item.timOrdtype] || []), item],
            }),
            { ...emptyTimingData }
          )
        );
      })
      .catch((error) => {
        setData(emptyTimingData);
        setLoadError(getApiErrorMessage(error, "No restaurant timing data found"));
        console.error("Failed to load rest times", error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatTime = (value: string) => {
    const date = new Date(value);
    return isNaN(date.getTime()) ? "--" : format(date, "hh:mm a");
  };

  const isValidTime = (value?: string) => {
    if (!value) return false;
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const getTimeRanges = (item: TimeItem) => {
    const ranges = [
      { label: "1", from: item.timFrom, to: item.timTo },
      { label: "2", from: item.timFrom1, to: item.timTo1 },
      { label: "3", from: item.timFrom2, to: item.timTo2 },
    ];

    return ranges.filter((range, index) => {
      if (index === 0) return true;
      return isValidTime(range.from) || isValidTime(range.to);
    });
  };

  const selectedRows = data?.[selectedTab] || [];
  const enabledCount = selectedRows.filter((item) => item.timEnable).length;
  const orderTypeIcon =
    selectedTab === ORDER.DELIVERY ? (
      <DeliveryDining className="!text-[22px]" />
    ) : (
      <Storefront className="!text-[22px]" />
    );

  return (
    <div className="w-full flex flex-col gap-4 p-4">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-gray-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-lg text-white"
              style={{ backgroundColor: themeColor }}
            >
              {orderTypeIcon}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedTab} Timing
              </h3>
              <p className="text-sm text-gray-500">
                {enabledCount} of {selectedRows.length} days enabled
              </p>
            </div>
          </div>

          <Tabs
            value={selectedTab}
            onChange={(_, i) => setSelectedTab(i)}
            aria-label="order timing tabs"
            className="self-start lg:self-center"
            TabIndicatorProps={{ className: "!hidden" }}
            sx={{
              minHeight: 0,
              backgroundColor: "#f3f4f6",
              borderRadius: "10px",
              padding: "4px",
              "& .MuiTab-root": {
                borderRadius: "8px",
                color: "#4b5563",
                fontSize: 13,
                fontWeight: 700,
                minHeight: 36,
                padding: "8px 18px",
                textTransform: "none",
              },
              "& .Mui-selected": {
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 4px rgba(15, 23, 42, 0.12)",
                color: `${themeColor} !important`,
              },
            }}
          >
            {Object.keys(data).map((i) => (
              <Tab
                key={i}
                icon={i === ORDER.DELIVERY ? <DeliveryDining /> : <Storefront />}
                iconPosition="start"
                label={i}
                value={i}
              />
            ))}
          </Tabs>
        </div>

        {loadError && !loading ? (
          <div className="px-5 py-8">
            <div className="mx-auto flex max-w-[620px] flex-col items-center rounded-xl border border-red-100 bg-red-50/80 px-5 py-7 text-center shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-red-600 shadow-sm">
                <ErrorOutline className="!text-[28px]" />
              </div>
              <h4 className="mt-4 text-[18px] font-bold text-gray-950">
                Restaurant timing is not configured
              </h4>
              <p className="mt-2 max-w-[480px] text-[14px] leading-6 text-gray-600">
                {loadError}
              </p>
              <button
                type="button"
                onClick={fetchData}
                className="mt-5 inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-[13px] font-bold text-red-700 shadow-sm transition hover:bg-red-50"
              >
                <Refresh className="!text-[18px]" />
                Retry
              </button>
            </div>
          </div>
        ) : (
        <div className="time-setting-table overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-5 py-3">Day</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Open 24hr</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">To</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {selectedRows.map((item) => (
                <tr
                  key={`${item.timOrdtype}-${item.timDay}`}
                  className="transition hover:bg-gray-50"
                >
                  <td className="px-5 py-4">
                    <div className="font-semibold text-gray-900">
                      {item.timDay}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.timOrdtype}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        item.timEnable
                          ? "bg-green-50 text-green-700 ring-1 ring-green-200"
                          : "bg-red-50 text-red-700 ring-1 ring-red-200"
                      }`}
                    >
                      {item.timEnable ? (
                        <CheckCircle className="!text-[15px]" />
                      ) : (
                        <DoNotDisturbOn className="!text-[15px]" />
                      )}
                      {item.timEnable ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                      {item.timIs24Hr ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {item.timIs24Hr ? (
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <AccessTime className="!text-[17px] text-gray-400" />
                        All day
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {getTimeRanges(item).map((range) => (
                          <div
                            key={`${item.timDay}-${range.label}-from`}
                            className="flex items-center gap-2 text-sm font-medium text-gray-800"
                          >
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-[11px] font-semibold text-gray-600">
                              {range.label}
                            </span>
                            <AccessTime className="!text-[16px] text-gray-400" />
                            {formatTime(range.from)}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {item.timIs24Hr ? (
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <AccessTime className="!text-[17px] text-gray-400" />
                        All day
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {getTimeRanges(item).map((range) => (
                          <div
                            key={`${item.timDay}-${range.label}-to`}
                            className="flex items-center gap-2 text-sm font-medium text-gray-800"
                          >
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-[11px] font-semibold text-gray-600">
                              {range.label}
                            </span>
                            <AccessTime className="!text-[16px] text-gray-400" />
                            {formatTime(range.to)}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition hover:border-gray-300 hover:bg-gray-100 hover:text-gray-900"
                      onClick={() => setSelectedData(item)}
                      aria-label={`Edit ${item.timDay} timing`}
                    >
                      <EditOutlined className="!text-[20px]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && !selectedRows.length && (
            <div className="bg-white p-4">
              {Array.from({ length: 7 }).map((_, index) => (
                <div
                  key={index}
                  className="mb-2 h-12 animate-pulse rounded-lg bg-gray-100"
                ></div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
      <TimeSettingDialog
        options={data}
        selected={selectedData}
        onClose={(v) => {
          if (v) {
            fetchData();
          }
          setSelectedData(undefined);
        }}
      />
    </div>
  );
};

export default TimeSetting;
